import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { customPayRunGateway } from '../index';
import { PayrollRunStatusBadge } from '../../payroll-review/components/PayrollRunStatusBadge';
import { LEGACY_STEP_KEYS, WIZARD_STEPS, runStatusLabel, runTypeLabel, type CustomPayRunResponse, type WizardStepKey } from '../types';
import { CustomPayrollStep } from './CustomPayrollStep';
import { ReviewPayrollStep } from './ReviewPayrollStep';
import { PendingPayrollStep } from './PendingPayrollStep';
import { ApprovedPayrollStep } from './ApprovedPayrollStep';
import { WizardStepper } from './WizardStepper';
import { stepAccess } from './stepAccess';
import { useApproverView } from './useApproverView';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import '../custom-pay-run.css';

export interface StepProps {
  run: CustomPayRunResponse;
  /** The viewer is another user who can approve this run (not its preparer or submitter). */
  approverView: boolean;
  refreshRun: () => Promise<void>;
  goToStep: (step: WizardStepKey) => void;
}

const STEP_COMPONENTS: Record<WizardStepKey, (props: StepProps) => ReactElement> = {
  'custom-payroll': CustomPayrollStep,
  'review-payroll': ReviewPayrollStep,
  'pending-payroll': PendingPayrollStep,
  'approved-payroll': ApprovedPayrollStep,
};

/** Route segment → step. The earlier 8-step segments still resolve (to the step that replaced them); anything else opens Step 1. */
function resolveStep(segment: string | undefined): WizardStepKey {
  if (WIZARD_STEPS.some((s) => s.key === segment)) return segment as WizardStepKey;
  return (segment && LEGACY_STEP_KEYS[segment]) || 'custom-payroll';
}

export function CustomPayRunWizard() {
  const { runId, step } = useParams<{ runId: string; step: string }>();
  const navigate = useNavigate();
  const [run, setRun] = useState<CustomPayRunResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshRun = useCallback(async () => {
    if (!runId) return;
    setError(null);
    try {
      setRun(await customPayRunGateway.getById(runId));
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load this custom pay run.'));
    }
  }, [runId]);

  const load = useCallback(async () => {
    setLoading(true);
    await refreshRun();
    setLoading(false);
  }, [refreshRun]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const approverView = useApproverView(run);
  const activeStep = resolveStep(step);
  const goToStep = useCallback((next: WizardStepKey) => navigate(`/payroll-processing/${runId}/${next}`), [navigate, runId]);

  if (loading) {
    return (
      <section className="card card--wide">
        <p className="muted">Loading…</p>
      </section>
    );
  }

  if (error || !run) {
    return (
      <section className="card card--wide">
        <p className="error">{error ?? 'Custom pay run not found.'}</p>
        <button type="button" className="link" onClick={() => navigate('/payroll-processing/history')}>
          Back to Custom Pay Runs
        </button>
      </section>
    );
  }

  const access = stepAccess(run)[activeStep];
  const StepComponent = STEP_COMPONENTS[activeStep];

  return (
    <section className="card card--wide cpr">
      <div className="section-header">
        <h2 className="cpr__title">Custom Pay Run</h2>
        <button type="button" className="link" onClick={() => navigate('/payroll-processing/history')}>
          Pay run history
        </button>
      </div>

      <p className="muted cpr__meta">
        <span>
          Run type: <strong data-testid="run-type-label">{runTypeLabel(run.runType)}</strong>
        </span>
        <span>
          Status: <strong>{runStatusLabel(run.status)}</strong>
        </span>
        {run.payrollRunStatus && (
          <span>
            Review status: <PayrollRunStatusBadge status={run.payrollRunStatus} />
          </span>
        )}
      </p>

      <WizardStepper run={run} activeStep={activeStep} onSelect={goToStep} approverView={approverView} />

      {access.available ? (
        // Keyed by step and run so a step never carries another step's (or another run's) local state.
        <StepComponent key={`${run.id}-${activeStep}`} run={run} approverView={approverView} refreshRun={refreshRun} goToStep={goToStep} />
      ) : (
        <div className="cpr-notice cpr-notice--warning" role="alert">
          <p>
            <strong>This step is not available yet.</strong> {access.reason}
          </p>
          <button type="button" className="cpr-btn" onClick={() => goToStep('custom-payroll')}>
            Back to Custom Payroll
          </button>
        </div>
      )}
    </section>
  );
}
